import { useRef, useEffect, useState, useCallback, memo } from 'react';

const MiniMap = ({ mainContainerId = '', itemsNumber = 0 }) => {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const targetRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [thumbWidth, setThumbWidth] = useState(0);

  const updateThumbSize = useCallback(() => {
    if (!targetRef.current || !trackRef.current) return;

    const target = targetRef.current;
    const track = trackRef.current;
    const rect = track.getBoundingClientRect();

    const containerWidth = rect?.width;
    const contentWidth = target.scrollWidth;
    const viewportWidth = target.clientWidth;

    if (contentWidth <= viewportWidth) {
      setThumbWidth(containerWidth);
      thumbRef.current.style.left = '0px';

      return;
    }

    const ratio = viewportWidth / contentWidth;
    const newThumbWidth = Math.max(ratio * containerWidth, 50);
    setThumbWidth(newThumbWidth);

    updateThumbPosition(target.scrollLeft);
  }, []);

  useEffect(() => {
    const target = document.getElementById(mainContainerId);
    if (target) {
      targetRef.current = target;
      updateThumbSize();
    }
  }, [mainContainerId, updateThumbSize]);

  const updateThumbPosition = useCallback(
    (scrollLeft) => {
      if (!targetRef.current || !trackRef.current) return;

      const target = targetRef.current;
      const track = trackRef.current;
      const rect = track.getBoundingClientRect();

      const containerWidth = rect?.width;
      const contentWidth = target.scrollWidth;
      const viewportWidth = target.clientWidth;

      if (contentWidth <= viewportWidth) {
        thumbRef.current.style.left = '0px';
        return;
      }

      const maxScroll = contentWidth - viewportWidth;
      const maxThumbTravel = containerWidth - thumbWidth;
      const ratio = scrollLeft / maxScroll;
      const newPos = ratio * maxThumbTravel;

      thumbRef.current.style.left =
        Math.max(0, Math.min(newPos, maxThumbTravel)) + 'px';
    },
    [thumbWidth]
  );

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const handleScroll = () => {
      updateThumbPosition(target.scrollLeft);
    };

    target.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateThumbSize);

    updateThumbSize();

    return () => {
      target.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumbSize);
    };
  }, [updateThumbSize, updateThumbPosition]);

  const handleTrackClick = (e) => {
    if (!trackRef.current || !targetRef.current || isDragging) return;
    if (e.target === thumbRef.current) return;

    const track = trackRef.current;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const centerThumb = clickX - thumbWidth / 2;

    const maxThumbTravel = rect?.width - thumbWidth;
    const clampedX = Math.max(0, Math.min(centerThumb, maxThumbTravel));
    thumbRef.current.style.left = clampedX + 'px';

    const target = targetRef.current;
    const maxScroll = target.scrollWidth - target.clientWidth;
    const ratio = clampedX / maxThumbTravel;
    target.scrollLeft = ratio * maxScroll;
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !trackRef.current || !targetRef.current) return;

      const track = trackRef.current;
      const rect = track.getBoundingClientRect();
      const newX =
        e.clientX - rect.left - (thumbRef.current?.offsetWidth || 0) / 2;

      const maxThumbTravel = rect?.width - thumbWidth;
      const clampedX = Math.max(0, Math.min(newX, maxThumbTravel));
      thumbRef.current.style.left = clampedX + 'px';

      const target = targetRef.current;
      const maxScroll = target.scrollWidth - target.clientWidth;
      const ratio = clampedX / maxThumbTravel;
      target.scrollLeft = ratio * maxScroll;
    },
    [isDragging, thumbWidth]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove]);

  return (
    <div
      className='fixed bottom-4 right-5 w-auto h-[80px] p-2 bg-gray-50 border border-gray-200 rounded-lg shadow-2xl z-50'
      style={{ backdropFilter: 'blur(8px)' }}
    >
      <div
        ref={trackRef}
        onClick={handleTrackClick}
        className='absolute z-50 top-0 left-0 w-full h-full cursor-pointer overflow-hidden'
      >
        <div
          ref={thumbRef}
          onMouseDown={handleMouseDown}
          className={`absolute z-20 top-0 h-full rounded-lg shadow-lg border-2 border-red-800 transition-all ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            width: `${thumbWidth}px`,
          }}
        />
      </div>
      <div
        className='!bg-white h-full flex items-center justify-between gap-2'
        id='minimap-items-wrapper'
      >
        {[...Array(itemsNumber).keys()].map((item) => (
          <div
            className='item !min-w-8 h-full !bg-gray-100'
            key={`minimap-item-${item}`}
          ></div>
        ))}
      </div>
    </div>
  );
};

export default memo(MiniMap);
